// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FeatureCollection } from 'geojson';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';
import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { setFilterData } from 'wdna-components/lib/mainfilter/actions';
import { parseDates, getBoundsOfDatesList } from 'wdna-components/lib/helpers/date';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableDO } from '../../../../configuration/requests';
import { sensor, SensorDO } from '../../../../sensors/requests';

interface Props {
  rowId?: number;
}

interface State {
  sensorArray: SensorDO[];
  typeVariableArray: TypeSensorVariableDO[];
  typeSensorId: number;

  dates: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  id?: number;
  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  sensorInfo?: SensorDO;
  variables: any[];
}

export default class Filter extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private firstRender: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.firstRender = true;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      sensorArray: [],
      typeVariableArray: [],
      typeSensorId: 3, // el 3 corresponde a ThermHygro Mobile

      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      variables: [],
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public componentDidUpdate(): void {
    const { firstRender } = this;
    if (firstRender) {
      this.firstRender = false;
      this.exportInitialData();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    const filter: Record<string, any> = {};
    filter.status = 'ON_AIR';
    filter.typeSensorId = this.state.typeSensorId;

    this.loadingStart();
    sensor<SensorDO[]>('list', undefined, filter)
      ?.then((resp: SensorDO[]) => {
        if (resp && resp.length > 0) {
          this.setLocalState(
            {
              id: resp[0].id,
              sensorArray: resp,
            }
            // this.loadVariables
          );
        } else {
          messageWarning('Warning', 'There are not mobile sensors in the system');
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public submit = async (): Promise<void> => {
    const { id, dateIni, timeIni, dateEnd, timeEnd, variables } = this.state;

    const idSensor: number | undefined = id || this.props.rowId;
    if (!idSensor) { messageError('Error', 'Please select a device'); return; }

    const dIni: string | undefined = (dateIni) ? moment(`${dateIni}T${timeIni || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;
    const dEnd: string | undefined = (dateEnd) ? moment(`${dateEnd}T${timeEnd || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString() : undefined;

    const req: Record<string, any> = {
      values: variables,
      from: dIni,
      to: dEnd,
    };

    // TODO: static to type 2 make dinamic to future mobile sensors
    req.lon = '4';
    req.lat = '5';
    req.values = [2, 3];
    req.alias = ['Temperature', 'Humidity'];

    if (this.isValidRequest(req)) {
      this.loadingStart();
      try {
        const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor)) || undefined;
        const mapData: FeatureCollection | undefined = await sensor<FeatureCollection>('gis_tracking', String(idSensor), req);
        if (sensorInfo) this.readingsLoad(sensorInfo, mapData);
      } catch (error) {
        messageError('Error', getErrorMessage(error));
      }
      this.loadingFinish();
    }
  };

  public isValidRequest = (data: Record<string, any>): boolean => {
    const { from, to } = data;
    if (!from || !to) {
      messageError('Error', 'Please select valid dates for the interval');
      return false;
    }
    return true;
  };

  public loadVariables = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;

    this.loadingStart();
    try {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor)) || undefined;
      if (sensorInfo) {
        const typeSensorId: number = sensorInfo.typeSensorId;
        const typeVariableArray: TypeSensorVariableDO[] = await typeSensorVariable<TypeSensorVariableDO[]>('list', undefined, { typeSensorId }) || [];
        this.setLocalState({
          sensorInfo,
          typeVariableArray,
          variables: ['2', '3'],
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public readingsLoad = (sensorInfo: SensorDO, mapData?: FeatureCollection): void => {
    this.context?.updateParent({
      update: window.getStr(),
      loading: true,
      mapData,
      sensorInfo,
    });
  };

  public loadingStart = (): void => {
    this.context?.updateParent({ loading: true, });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({ loading: false, });
    window.store.dispatch(finishProcess(this.pid));
  };

  public exportInitialData = (): void => {
    this.setLocalState({}, () => {
      this.notifyFilterChange();
      this.exportMainFilter();
    });
  };

  public exportMainFilter = (): void => {
    const mainFilter: any = this.getFilterState();

    this.context?.updateParent({
      mainFilter,
    });

    window.store.dispatch(setFilterData(mainFilter));
  };

  public notifyFilterChange = (): void => {
    this.context?.updateParent({
      onFilterChange: this.getFilterState(),
    });
  };

  public getFilterState = (): void => {
    const { state } = this;

    const mainFilter: any = _.cloneDeep(state);

    const { groupDateTime, flatpickrMode, date } = mainFilter;

    const options: any = {
      granularity: groupDateTime,
      mode: flatpickrMode,
    };

    mainFilter.arrFilterDate = parseDates(date, options);

    if (flatpickrMode === 'multiple') {
      const boundDates: any = getBoundsOfDatesList(mainFilter.arrFilterDate);
      mainFilter.arrFilterDate = boundDates;
    }

    /* clean the filter */
    return mainFilter;
  };

  /* Handlers */
  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  /* Management values of multiple select */
  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setLocalState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setLocalState({
      variables: selection,
    });
  };

  public handleFilterBtnClick = (): void => {
    this.exportMainFilter();
    this.submit().catch(() => { /* pass */ });
  };

  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    const { flatpickrMode } = this.state;

    const hours: string[] = config.getHours() as string[];
    const dates: Date[] = config.getDates() as Date[];
    const newDataIni: moment.Moment = moment(dates[0]);
    const newDataEnd: moment.Moment = (flatpickrMode === 'range') ? moment(dates[1]) : moment(newDataIni).add(1, 'days');

    this.setLocalState({
      dates,
      timeIni: hours[0],
      timeEnd: hours[1],
      dateIni: newDataIni.format('YYYY-MM-DD'),
      dateEnd: newDataEnd.format('YYYY-MM-DD'),
      flatpickrMode: config.getMode(),
    });
  };

  public render(): JSX.Element {
    const { sensorArray, typeVariableArray, variables } = this.state;
    const { id, dates, flatpickrMode, groupDateTime } = this.state;

    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              value={id}
              onChange={this.handleChange}
              data-ctype="number"
              data={sensorArray.map((item: SensorDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              options={{ placeholder: 'Select Sensor' }}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <UiValidate>
          {false && (
            <div className="sideFilter" style={{ marginBottom: '15px' }}>
              <Select2
                multiple
                name="variables"
                value={variables}
                onSelect={this.handleChangeMultiple}
                onUnselect={this.handleChangeMultiple}
                data={typeVariableArray.map((item: TypeSensorVariableDO, idx: number): DataSelect2 => {
                  return {
                    id: idx,
                    text: `${item.variableName}`,
                    title: `${item.variableName} [D${idx}] [${item.variableUnit}]`,
                  };
                })}
                options={{ placeholder: 'Select variables' }}
                style={{ width: '99%' }}
                required
              />
            </div>
          )}
          <DatePicker
            icon
            hours={[]}
            dates={dates}
            showChangeModes
            mode={flatpickrMode}
            granularity={groupDateTime}
            iconClass="wdna-ico-icon-calendar"
            handleChanges={this.handleChangeDatePicker}
          />
          <div style={{ marginTop: '1px' }}>
            <button type="button" className="filter-btn btn btn-default" onClick={this.handleFilterBtnClick}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </UiValidate>
      </div>
    );
  }
}
